// Los pagos del cobrador C-01 portados del `<script>` del mockup portal-cobrador-pagos.html
// (SK.cobranza.pagos + SK.clients + SK.credits + el historial de contactos).
//
// El mock ancla todo a un «hoy» fijo (2026-07-06) y arma sus fechas como desplazamientos contra él.
// Aquí se conserva el desplazamiento y el ancla pasa a ser el día real: con la fecha del mock
// congelada, Hoy · Semana · Mes marcarían cero y la pantalla se leería rota.
import { mxDayOffset } from '@/lib/dateHelpers';
import { shiftDay } from './logic';
import type {
    CollectorPaymentRow,
    PaymentChannel,
    PaymentContactEntry,
    PaymentPromise,
} from './types';

/** CDMX no cambia de huso desde 2022, así que el desfase es fijo y la hora del mock se conserva. */
const MX_UTC_OFFSET = '-06:00';

/** Cuántos días baja la mora un pago aplicado (`dpiDespues` simulado del mock). */
const ARREARS_DAYS_CURED = 7;

interface PaymentSeed {
    paymentId: string;
    creditId: string;
    customerId: string;
    customerName: string;
    /** Días antes de hoy, como el `addDays(HOY, -n)` del mock. */
    dayOffset: number;
    time: string;
    amount: number;
    channel: PaymentChannel;
    arrearsDaysBefore: number;
    promise: { promiseId: string; amount: number; dueDayOffset: number } | null;
}

interface ContactSeed {
    creditId: string;
    dayOffset: number;
    time: string;
    channel: string;
    outcome: string;
    note: string;
}

const PAYMENT_SEEDS: readonly PaymentSeed[] = [
    {
        paymentId: 'PG-4001',
        creditId: 'CR-3006',
        customerId: 'CL-3006',
        customerName: 'Miguel Ángel Reyes',
        dayOffset: 0,
        time: '09:15',
        amount: 260,
        channel: 'store',
        arrearsDaysBefore: 22,
        promise: { promiseId: 'PR-2002', amount: 260, dueDayOffset: 0 },
    },
    {
        paymentId: 'PG-4002',
        creditId: 'CR-3003',
        customerId: 'CL-3003',
        customerName: 'Ana María Ruiz',
        dayOffset: 0,
        time: '11:30',
        amount: 320,
        channel: 'oxxo',
        arrearsDaysBefore: 5,
        promise: null,
    },
    {
        paymentId: 'PG-4003',
        creditId: 'CR-3011',
        customerId: 'CL-3011',
        customerName: 'Rosa Elena Mendoza',
        dayOffset: 0,
        time: '12:45',
        amount: 250,
        channel: 'app',
        arrearsDaysBefore: 0,
        promise: null,
    },
    {
        paymentId: 'PG-4004',
        creditId: 'CR-3007',
        customerId: 'CL-3007',
        customerName: 'Patricia Domínguez',
        dayOffset: 0,
        time: '14:10',
        amount: 220,
        channel: 'store',
        arrearsDaysBefore: 1,
        promise: null,
    },
    {
        paymentId: 'PG-4005',
        creditId: 'CR-3009',
        customerId: 'CL-3009',
        customerName: 'Beatriz Salazar',
        dayOffset: -1,
        time: '10:00',
        amount: 380,
        channel: 'oxxo',
        arrearsDaysBefore: 8,
        promise: null,
    },
    {
        paymentId: 'PG-4006',
        creditId: 'CR-3007',
        customerId: 'CL-3007',
        customerName: 'Patricia Domínguez',
        dayOffset: -1,
        time: '16:20',
        amount: 220,
        channel: 'store',
        arrearsDaysBefore: 1,
        promise: { promiseId: 'PR-2008', amount: 220, dueDayOffset: -1 },
    },
    {
        paymentId: 'PG-4007',
        creditId: 'CR-3002',
        customerId: 'CL-3002',
        customerName: 'Ricardo Pérez López',
        dayOffset: -3,
        time: '12:00',
        amount: 460,
        channel: 'app',
        arrearsDaysBefore: 15,
        promise: null,
    },
    {
        paymentId: 'PG-4008',
        creditId: 'CR-3005',
        customerId: 'CL-3005',
        customerName: 'Laura Villareal',
        dayOffset: -4,
        time: '09:30',
        amount: 260,
        channel: 'oxxo',
        arrearsDaysBefore: 3,
        promise: null,
    },
    {
        paymentId: 'PG-4009',
        creditId: 'CR-3004',
        customerId: 'CL-3004',
        customerName: 'Fernando Ortega',
        dayOffset: -5,
        time: '15:00',
        amount: 620,
        channel: 'store',
        arrearsDaysBefore: 62,
        promise: null,
    },
];

const CONTACT_SEEDS: readonly ContactSeed[] = [
    {
        creditId: 'CR-3002',
        dayOffset: 0,
        time: '10:15',
        channel: 'llamada',
        outcome: 'promesa',
        note: 'Pagará $460 en OXXO hoy mismo',
    },
    {
        creditId: 'CR-3002',
        dayOffset: -2,
        time: '09:20',
        channel: 'llamada',
        outcome: 'hablo',
        note: 'Cliente indica quincena el 8',
    },
    {
        creditId: 'CR-3003',
        dayOffset: -1,
        time: '14:30',
        channel: 'chat',
        outcome: 'hablo',
        note: 'Confirmó pago OXXO',
    },
    {
        creditId: 'CR-3004',
        dayOffset: 0,
        time: '13:00',
        channel: 'llamada',
        outcome: 'promesa',
        note: '$620 hoy vía OXXO. Segundo compromiso.',
    },
    {
        creditId: 'CR-3004',
        dayOffset: -3,
        time: '11:00',
        channel: 'llamada',
        outcome: 'hablo',
        note: 'Solicita reestructura por baja de ingresos',
    },
    {
        creditId: 'CR-3004',
        dayOffset: -6,
        time: '09:00',
        channel: 'llamada',
        outcome: 'no_contesta',
        note: '',
    },
    {
        creditId: 'CR-3005',
        dayOffset: -6,
        time: '09:00',
        channel: 'sms',
        outcome: 'enviado',
        note: 'Aviso automático',
    },
    {
        creditId: 'CR-3006',
        dayOffset: 0,
        time: '08:45',
        channel: 'chat',
        outcome: 'hablo',
        note: 'Cliente reporta pago hecho en tienda',
    },
    {
        creditId: 'CR-3007',
        dayOffset: 0,
        time: '09:00',
        channel: 'llamada',
        outcome: 'promesa',
        note: 'Pagará hoy en tienda',
    },
    {
        creditId: 'CR-3009',
        dayOffset: -1,
        time: '13:00',
        channel: 'llamada',
        outcome: 'promesa',
        note: 'App mañana temprano',
    },
];

function instantOf(today: string, dayOffset: number, time: string): string {
    return `${shiftDay(today, dayOffset)}T${time}:00${MX_UTC_OFFSET}`;
}

function contactHistoryOf(creditId: string, today: string): PaymentContactEntry[] {
    return CONTACT_SEEDS.filter((seed) => seed.creditId === creditId)
        .map((seed) => ({
            at: instantOf(today, seed.dayOffset, seed.time),
            channel: seed.channel,
            outcome: seed.outcome,
            note: seed.note,
        }))
        .sort((left, right) => right.at.localeCompare(left.at));
}

function promiseOf(seed: PaymentSeed, today: string): PaymentPromise | null {
    if (seed.promise === null) return null;
    return {
        promiseId: seed.promise.promiseId,
        amount: seed.promise.amount,
        dueDay: shiftDay(today, seed.promise.dueDayOffset),
    };
}

/** Los pagos del cobrador contra el día que se le pase; `today` entra para que los tests lo fijen. */
export function buildCollectorPayments(
    today: string = mxDayOffset(0),
): CollectorPaymentRow[] {
    return PAYMENT_SEEDS.map((seed) => ({
        paymentId: seed.paymentId,
        creditId: seed.creditId,
        customerId: seed.customerId,
        customerName: seed.customerName,
        receivedAt: instantOf(today, seed.dayOffset, seed.time),
        amount: seed.amount,
        channel: seed.channel,
        promise: promiseOf(seed, today),
        arrearsDaysBefore: seed.arrearsDaysBefore,
        arrearsDaysAfter: Math.max(0, seed.arrearsDaysBefore - ARREARS_DAYS_CURED),
        appliedInstallment: seed.amount,
        contactHistory: contactHistoryOf(seed.creditId, today),
    }));
}
